import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getCurrentSubscription, subscribeToPush, unsubscribeFromPush } from "../lib/push";
import { useI18n } from "../i18n";

// Compact bell button — shows current push status, toggles subscription on
// click. Hidden entirely when the browser doesn't support push (iOS Safari
// <16.4, older browsers).
export default function PushToggleButton() {
    const { t } = useI18n();
    const [supported, setSupported] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        if (!pushSupported()) {
            setSupported(false);
            return;
        }
        setSupported(true);
        const sub = await getCurrentSubscription();
        setEnabled(!!sub && Notification.permission === "granted");
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    if (!supported) return null;

    const toggle = async () => {
        setBusy(true);
        try {
            if (enabled) {
                await unsubscribeFromPush();
                setEnabled(false);
                toast.success(t("push.toast.off"));
            } else {
                await subscribeToPush();
                setEnabled(true);
                toast.success(t("push.toast.on"));
            }
        } catch (e) {
            toast.error(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={toggle}
            disabled={busy}
            data-testid="push-toggle-btn"
            aria-label={enabled ? t("push.disable") : t("push.enable")}
            title={enabled ? t("push.disable") : t("push.enable")}
            className={`relative p-2 rounded-md transition-colors ${
                enabled
                    ? "text-[var(--accent-hex)] bg-[var(--accent-hex)]/10 hover:bg-[var(--accent-hex)]/20"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            }`}
        >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : enabled ? <Bell className="h-4 w-4" />
                : <BellOff className="h-4 w-4" />}
        </button>
    );
}
